<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 14px;">
            <div class="header-icon-wrap"><i class="fe fe-briefcase" style="font-size: 22px;"></i></div>
            <div>
              <h4 class="page-title text-white">My practice</h4>
              <small style="opacity: .8;">{{ facilityName }} · independent facility</small>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">
        <div v-if="!row && loaded" class="alert alert-warning">This page is for facilities that registered themselves on Claimify.</div>

        <template v-if="row">
          <IndependentStatusBanner :summary="summary" class="mb-4" />

          <div class="row">
            <div class="col-lg-6 mb-4">
              <div class="card shadow-sm form-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-video" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 text-white">Telemedicine fee</h6>
                </div>
                <form class="card-body px-4 py-4" @submit.prevent="save">
                  <p class="small text-muted">
                    What a patient pays for one telemedicine consultation. Patients pay online with Paystack from the link in
                    their booking email before they can join. Leave it at 0 for free consultations.
                  </p>
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label for="mp-fee" class="small font-weight-bold text-secondary">Consultation fee (₦)</label>
                      <input id="mp-fee" v-model.number="fee" type="number" min="0" step="100" class="form-control" :disabled="!canEdit" />
                    </div>
                    <div class="form-group col-md-6">
                      <label for="mp-licence" class="small font-weight-bold text-secondary">Practice licence number</label>
                      <input id="mp-licence" v-model="licence" class="form-control" :disabled="!canEdit" />
                    </div>
                  </div>
                  <div class="small mb-3" :class="row.payments_to_facility ? 'text-success' : 'text-muted'">
                    <i class="fe mr-1" :class="row.payments_to_facility ? 'fe-check-circle' : 'fe-info'"></i>
                    <span v-if="row.payments_to_facility">Payments settle directly to your facility's Paystack account.</span>
                    <span v-else>Payments are collected by Claimify and settled to you. Ask support to link your Paystack account for direct settlement.</span>
                  </div>
                  <button v-if="canEdit" type="submit" class="btn btn-submit" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm mr-1" role="status"></span> Save
                  </button>
                  <p v-else class="small text-muted mb-0">Only the facility account or a facility admin can change these.</p>
                </form>
              </div>
            </div>

            <div class="col-lg-6 mb-4">
              <div class="card shadow-sm form-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-check-square" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 text-white">Set up your practice</h6>
                </div>
                <ul class="p-0 m-0">
                  <li v-for="step in steps" :key="step.to" class="info-row">
                    <i class="info-icon" :class="step.icon"></i>
                    <div class="flex-grow-1">
                      <router-link :to="step.to" class="info-value d-block">{{ step.title }}</router-link>
                      <p class="small text-muted mb-0">{{ step.text }}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </template>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import IndependentStatusBanner from "./IndependentStatusBanner.vue";
import { saveIndependent } from "@/service/independent";

export default {
  components: { Navbar, IndependentStatusBanner },
  data() {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return {
      user,
      row: null,
      summary: null,
      loaded: false,
      fee: 0,
      licence: "",
      saving: false,
      steps: [
        { to: "/manage-services", icon: "fe fe-list", title: "Price list: services", text: "Consultations, procedures and tests you bill for." },
        { to: "/manage-drugs", icon: "fe fe-package", title: "Price list: drugs", text: "Drugs you dispense, with prices." },
        { to: { path: "/provider/add-employee/" + user.id }, icon: "fe fe-users", title: "Staff", text: "Doctors and staff who sign in for your facility." },
        { to: "/telemedicine", icon: "fe fe-clock", title: "Consultation hours", text: "When patients can be booked for telemedicine." },
        { to: "/my-patients", icon: "fe fe-user-plus", title: "Patients", text: "Enrol patients and their dependants (after approval)." },
        { to: "/marketplace", icon: "fe fe-shopping-cart", title: "Buy supplies", text: "Order drugs and equipment from vendors (after approval)." },
      ],
    };
  },
  computed: {
    facilityName() {
      return this.user.agency_name || "";
    },
    canEdit() {
      return this.user.type === "provider" || Number(this.user.user_role) === 1;
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.axios
        .get("/api/v1/auth/independent/me")
        .then((res) => this.apply(res.data))
        .catch(() => { this.row = null; })
        .finally(() => { this.loaded = true; });
    },
    apply(data) {
      this.row = data.data;
      this.summary = data.summary;
      this.fee = Number(data.data.consultation_fee) || 0;
      this.licence = data.data.licence_number || "";
      saveIndependent(data.summary);
    },
    save() {
      this.saving = true;
      this.axios
        .put("/api/v1/auth/independent/me", { consultation_fee: this.fee || 0, licence_number: this.licence })
        .then((res) => {
          this.apply(res.data);
          this.$toasted.success("Saved", { position: "top-center", duration: 2500 });
        })
        .catch((err) => {
          const body = (err.response && err.response.data) || {};
          this.$toasted.error(body.errors ? Object.values(body.errors).flat().join(" ") : body.message || "Could not save", { position: "top-center", duration: 4000 });
        })
        .finally(() => { this.saving = false; });
    },
  },
};
</script>
